/**
 * End-to-End Encryption utilities using the Web Crypto API.
 *
 * Implements hybrid encryption:
 *   - RSA-OAEP 2048-bit for wrapping a per-message AES key
 *   - AES-GCM 256-bit for encrypting the message content
 *
 * Private keys are stored in localStorage per user ID.
 * The server only ever sees opaque ciphertext and RSA-wrapped AES keys.
 *
 * @module crypto
 */

const PRIVATE_KEY_PREFIX = 'e2ee_private_';
const PUBLIC_KEY_PREFIX = 'e2ee_public_';

const RSA_PARAMS: RsaHashedKeyGenParams = {
  name: 'RSA-OAEP',
  modulusLength: 2048,
  publicExponent: new Uint8Array([1, 0, 1]),
  hash: 'SHA-256',
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function bufToB64(buf: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buf)));
}

function b64ToBuf(b64: string): ArrayBuffer {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

// ── Key generation & storage ─────────────────────────────────────────────────

async function generateKeyPair(): Promise<CryptoKeyPair> {
  return crypto.subtle.generateKey(RSA_PARAMS, true, ['encrypt', 'decrypt']);
}

async function exportPublicKeyB64(key: CryptoKey): Promise<string> {
  return bufToB64(await crypto.subtle.exportKey('spki', key));
}

async function exportPrivateKeyB64(key: CryptoKey): Promise<string> {
  return bufToB64(await crypto.subtle.exportKey('pkcs8', key));
}

async function importPublicKey(b64: string): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    'spki',
    b64ToBuf(b64),
    { name: 'RSA-OAEP', hash: 'SHA-256' },
    false,
    ['encrypt'],
  );
}

async function importPrivateKey(b64: string): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    'pkcs8',
    b64ToBuf(b64),
    { name: 'RSA-OAEP', hash: 'SHA-256' },
    false,
    ['decrypt'],
  );
}

/**
 * Return the RSA key pair for userId from localStorage, generating and storing
 * a new pair if none exists yet.
 *
 * Returns both the base64 public key (ready to upload to server) and the
 * CryptoKey private key (ready for decryption).
 */
export async function getOrCreateKeyPair(
  userId: string,
): Promise<{ publicKeyB64: string; privateKey: CryptoKey }> {
  const storedPrivate = localStorage.getItem(`${PRIVATE_KEY_PREFIX}${userId}`);
  const storedPublic = localStorage.getItem(`${PUBLIC_KEY_PREFIX}${userId}`);

  if (storedPrivate && storedPublic) {
    try {
      const privateKey = await importPrivateKey(storedPrivate);
      return { publicKeyB64: storedPublic, privateKey };
    } catch {
      // Corrupted entry — fall through to regenerate
    }
  }

  const keyPair = await generateKeyPair();
  const publicKeyB64 = await exportPublicKeyB64(keyPair.publicKey);
  const privateKeyB64 = await exportPrivateKeyB64(keyPair.privateKey);

  localStorage.setItem(`${PRIVATE_KEY_PREFIX}${userId}`, privateKeyB64);
  localStorage.setItem(`${PUBLIC_KEY_PREFIX}${userId}`, publicKeyB64);

  return { publicKeyB64, privateKey: keyPair.privateKey };
}

/**
 * Return only the stored private key for userId, or null if not available.
 * Does NOT generate a new pair — use getOrCreateKeyPair for that.
 */
export async function getPrivateKey(userId: string): Promise<CryptoKey | null> {
  const stored = localStorage.getItem(`${PRIVATE_KEY_PREFIX}${userId}`);
  if (!stored) return null;
  try {
    return await importPrivateKey(stored);
  } catch {
    return null;
  }
}

// ── Encryption & Decryption ───────────────────────────────────────────────────

export interface EncryptedPayload {
  encryptedContent: string;
  encryptedKeyForSender: string;
  encryptedKeyForRecipient: string;
}

/**
 * Encrypt a plain-text message for a conversation between two users.
 *
 * The AES key is wrapped with both the recipient's and sender's public keys so
 * both parties can decrypt the message independently.
 *
 * @param content - Plain-text message body
 * @param recipientPublicKeyB64 - Base64 SPKI key of the recipient
 * @param senderPublicKeyB64 - Base64 SPKI key of the sender
 */
export async function encryptMessage(
  content: string,
  recipientPublicKeyB64: string,
  senderPublicKeyB64: string,
): Promise<EncryptedPayload> {
  // Generate a one-time AES-GCM key
  const aesKey = await crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt'],
  );

  const iv = crypto.getRandomValues(new Uint8Array(12));

  // Encrypt content
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    aesKey,
    new TextEncoder().encode(content),
  );

  // Export the raw AES key so it can be RSA-wrapped
  const rawAes = await crypto.subtle.exportKey('raw', aesKey);

  // Wrap AES key for both parties
  const [recipientPubKey, senderPubKey] = await Promise.all([
    importPublicKey(recipientPublicKeyB64),
    importPublicKey(senderPublicKeyB64),
  ]);

  const [encKeyForRecipient, encKeyForSender] = await Promise.all([
    crypto.subtle.encrypt({ name: 'RSA-OAEP' }, recipientPubKey, rawAes),
    crypto.subtle.encrypt({ name: 'RSA-OAEP' }, senderPubKey, rawAes),
  ]);

  // Bundle IV + ciphertext as a single base64 blob
  const encryptedContent = btoa(
    JSON.stringify({
      iv: bufToB64(iv.buffer),
      ct: bufToB64(ciphertext),
    }),
  );

  return {
    encryptedContent,
    encryptedKeyForRecipient: bufToB64(encKeyForRecipient),
    encryptedKeyForSender: bufToB64(encKeyForSender),
  };
}

/**
 * Decrypt a message encrypted with encryptMessage.
 *
 * @param encryptedContent - Base64-encoded JSON blob produced by encryptMessage
 * @param encryptedKey - The RSA-wrapped AES key for the calling user
 * @param privateKey - The calling user's RSA private CryptoKey
 * @returns Plain-text message body
 */
export async function decryptMessage(
  encryptedContent: string,
  encryptedKey: string,
  privateKey: CryptoKey,
): Promise<string> {
  // Unwrap AES key
  const rawAes = await crypto.subtle.decrypt(
    { name: 'RSA-OAEP' },
    privateKey,
    b64ToBuf(encryptedKey),
  );

  const aesKey = await crypto.subtle.importKey(
    'raw',
    rawAes,
    { name: 'AES-GCM' },
    false,
    ['decrypt'],
  );

  // Decode content bundle
  const { iv: ivB64, ct: ctB64 } = JSON.parse(atob(encryptedContent)) as {
    iv: string;
    ct: string;
  };

  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: new Uint8Array(b64ToBuf(ivB64)) },
    aesKey,
    b64ToBuf(ctB64),
  );

  return new TextDecoder().decode(plaintext);
}
